import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent, DialogFooterComponent, DialogHeaderComponent } from '@akostm/orbit-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ActionKind,
  TriggerKind,
  Workflow,
  WorkflowAction,
  WorkflowTrigger,
} from '@clientwire/api';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { WorkflowDialogService } from './workflow-dialog.service';
import { RuleEditorComponent } from '../rule-editor/rule-editor.component';

export interface WorkflowDialogData {
  title?: string;
  workflow?: Workflow;
  cancelLabel?: string;
  confirmLabel?: string;
  message?: string;
  isConfirmation?: boolean;
}

@Component({
  selector: 'lib-workflow-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    RuleEditorComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './workflow-dialog.component.html',
  styleUrl: './workflow-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowDialogComponent implements OnInit, AfterViewInit {
  readonly #dialogRef = inject(DialogRef);
  readonly #formBuilder = inject(FormBuilder);
  readonly #translateService = inject(TranslateService);
  readonly #workflowDialogService = inject(WorkflowDialogService);

  // Form selections
  readonly triggerKinds = this.#workflowDialogService.triggerKinds;
  readonly actionKinds = this.#workflowDialogService.actionKinds;
  readonly conditionOperators = this.#workflowDialogService.conditionOperators;

  readonly conversationTypes = this.#workflowDialogService.conversationTypes;
  readonly messageTemplates = this.#workflowDialogService.messageTemplates;

  readonly jsonPathPrefix = '$.conversation.conversation_data.';
  @ViewChild('jsonPathInput') jsonPathInput?: ElementRef<HTMLInputElement>;

  readonly data: WorkflowDialogData = inject(DIALOG_DATA);

  workflowForm!: FormGroup;
  isEditMode = false;

  readonly isConfirmation = signal(false);
  readonly errorMessage: WritableSignal<string | null> = signal(null);

  // Rule editor signals
  readonly ruleExpression = signal<string>('');
  readonly ruleValid = signal<boolean>(false);
  readonly ruleEditorErrors = signal<string[]>([]);

  ngOnInit(): void {
    this.isConfirmation.set(!!this.data.isConfirmation || !!this.data.message);

    if (!this.isConfirmation()) {
      this.isEditMode = !!this.data.workflow;

      this.data.title =
        this.data.title ||
        this.#translateService.instant(
          this.isEditMode ? 'SETTINGS.WORKFLOWS.EDIT_WORKFLOW' : 'SETTINGS.WORKFLOWS.ADD_WORKFLOW'
        );

      this.data.cancelLabel =
        this.data.cancelLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CANCEL_LABEL');

      this.data.confirmLabel =
        this.data.confirmLabel ||
        this.#translateService.instant(
          this.isEditMode ? 'SETTINGS.WORKFLOWS.SAVE' : 'SETTINGS.WORKFLOWS.CREATE'
        );

      this.#workflowDialogService.loadMessageTemplates();
      this.#workflowDialogService.loadConversationTypes();
      this.initForm();
    } else {
      this.data.cancelLabel =
        this.data.cancelLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CANCEL_LABEL');

      this.data.confirmLabel =
        this.data.confirmLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CONFIRM_LABEL');
      this.initForm();
    }
  }

  ngAfterViewInit(): void {
    this.setupJsonPathPrefixHandler();

    // Manually validate the rule expression after view initialization
    setTimeout(() => {
      if (
        this.isEditMode &&
        this.workflowForm.get('trigger.triggerKind')?.value ===
          TriggerKind.ConversationDataChanged &&
        this.ruleExpression()
      ) {
        // Parse the expression to verify it's valid
        try {
          const parseResult = this.#workflowDialogService.parseExpression(this.ruleExpression());
          if (parseResult.branches.length > 0) {
            // If parsing was successful, update the validity
            this.ruleValid.set(true);
            this.ruleEditorErrors.set([]);
          }
        } catch (error) {
          console.error('Error parsing rule expression:', error);
        }
      }
    }, 500); // Give the component time to fully initialize
  }

  initForm(): void {
    const workflow = this.data.workflow;
    const triggerKind = workflow?.trigger?.kind || TriggerKind.ConversationCreated;
    const actionKind = workflow?.trigger.action?.kind || ActionKind.NotifyParticipants;

    this.workflowForm = this.#formBuilder.group({
      conversationTypeId: [workflow?.conversationTypeId || ''],
      trigger: this.#formBuilder.group({
        triggerKind: [triggerKind, Validators.required],
        // Message Sent Trigger specific fields
        messageSequence: [this.getMessageSequence(workflow?.trigger)],
        participantKind: [this.getParticipantKind(workflow?.trigger)],
      }),
      action: this.#formBuilder.group({
        actionKind: [actionKind, Validators.required],
        // Send Message Action specific fields
        templateName: [this.getTemplateName(workflow?.trigger?.action)],
      }),
    });

    // Initialize rule expression if editing a workflow with CONVERSATION_DATA_CHANGED trigger
    if (this.isEditMode && triggerKind === TriggerKind.ConversationDataChanged && workflow) {
      // The ConversationDataChangedTrigger has branches inside the trigger object
      const dataChangedTrigger = workflow.trigger as any;

      // Extract branches and action from the trigger
      const branches = dataChangedTrigger.branches || [];
      const fallbackAction = dataChangedTrigger.action;

      const ruleExpr = this.#workflowDialogService.generateRuleExpression(branches, fallbackAction);
      this.ruleExpression.set(ruleExpr);
    }

    // Set up form control listeners
    this.workflowForm.get('trigger.triggerKind')?.valueChanges.subscribe(() => {
      this.updateTriggerFormValidation();
    });

    this.workflowForm.get('action.actionKind')?.valueChanges.subscribe(() => {
      this.updateActionFormValidation();
    });

    // Initialize validators
    this.updateTriggerFormValidation();
    this.updateActionFormValidation();
  }

  private getMessageSequence(trigger?: WorkflowTrigger): number | null {
    if (trigger?.kind === TriggerKind.MessageAddedToConversation) {
      return (trigger as any).messageSequence || 0;
    }
    return null;
  }

  private getParticipantKind(trigger?: WorkflowTrigger): string | null {
    if (trigger?.kind === TriggerKind.MessageAddedToConversation) {
      return (trigger as any).participantKind || null;
    }
    return null;
  }

  private getTemplateName(action?: WorkflowAction | null): string | null {
    if (action?.kind === ActionKind.SendMessage) {
      return (action as any).templateName || null;
    }
    return null;
  }

  private updateTriggerFormValidation(): void {
    const triggerKind = this.workflowForm.get('trigger.triggerKind')?.value;
    const triggerGroup = this.workflowForm.get('trigger') as FormGroup;

    // Reset all validations
    Object.keys(triggerGroup.controls).forEach((key) => {
      if (key !== 'triggerKind') {
        triggerGroup.get(key)?.clearValidators();
        triggerGroup.get(key)?.updateValueAndValidity();
      }
    });

    // Set validations based on trigger type
    if (triggerKind === TriggerKind.MessageAddedToConversation) {
      triggerGroup.get('messageSequence')?.setValidators([Validators.required, Validators.min(0)]);
    }

    // Update validations
    Object.keys(triggerGroup.controls).forEach((key) => {
      if (key !== 'triggerKind') {
        triggerGroup.get(key)?.updateValueAndValidity();
      }
    });
  }

  private updateActionFormValidation(): void {
    const actionKind = this.workflowForm.get('action.actionKind')?.value;
    const actionGroup = this.workflowForm.get('action') as FormGroup;

    // Reset all validations
    Object.keys(actionGroup.controls).forEach((key) => {
      if (key !== 'actionKind') {
        actionGroup.get(key)?.clearValidators();
        actionGroup.get(key)?.updateValueAndValidity();
      }
    });

    // Set validations based on action type
    if (actionKind === ActionKind.SendMessage) {
      actionGroup.get('templateName')?.setValidators([Validators.required]);
    }

    // Update validations
    Object.keys(actionGroup.controls).forEach((key) => {
      if (key !== 'actionKind') {
        actionGroup.get(key)?.updateValueAndValidity();
      }
    });
  }

  showField(formPath: string): boolean {
    const pathParts = formPath.split('.');
    if (pathParts.length < 2) return false;

    const groupName = pathParts[0];
    const fieldName = pathParts[1];

    if (groupName === 'trigger') {
      const triggerKind = this.workflowForm.get('trigger.triggerKind')?.value;

      if (fieldName === 'messageSequence' || fieldName === 'participantKind') {
        return triggerKind === TriggerKind.MessageAddedToConversation;
      }
    }

    if (groupName === 'action') {
      const actionKind = this.workflowForm.get('action.actionKind')?.value;

      if (fieldName === 'templateName') {
        return actionKind === ActionKind.SendMessage;
      }
    }

    return false;
  }

  closeDialog(save: boolean): void {
    if (this.isConfirmation()) {
      this.#dialogRef.close(save);
      return;
    }

    if (!save) {
      this.#dialogRef.close(null);
      return;
    }

    if (this.workflowForm.valid) {
      // Process form data for API
      const formValue = this.workflowForm.value;

      // Prepare the trigger object
      const trigger: any = {
        kind: formValue.trigger.triggerKind,
      };

      // Add specific fields based on trigger kind
      if (formValue.trigger.triggerKind === TriggerKind.MessageAddedToConversation) {
        trigger.messageSequence = formValue.trigger.messageSequence;
        if (formValue.trigger.participantKind) {
          trigger.participantKind = formValue.trigger.participantKind;
        }
      }

      if (formValue.trigger.triggerKind === TriggerKind.ConversationDataChanged) {
        // For conversation data changed, parse the rule expression and set the branches
        const ruleExpr = this.ruleExpression();

        const parseResult = this.#workflowDialogService.parseExpression(ruleExpr);

        // If the rule is valid, include the branches and fallback action in the result
        if (parseResult.branches.length > 0) {
          // Return the workflow object with parsed rule branches
          // For ConversationDataChangedTrigger, branches go inside the trigger
          const result: any = {
            conversationTypeId: formValue.conversationTypeId || null,
            trigger: {
              kind: formValue.trigger.triggerKind,
              branches: parseResult.branches,
              action: parseResult.elseAction || null,
            },
          };

          this.#dialogRef.close(result);
          return;
        } else {
          // If no branches, show an error
          this.errorMessage.set('Invalid rule expression');
          return;
        }
      } else {
        // For all other trigger kinds (except ConversationDataChanged)
        // Create the action object
        const action: any = {
          kind: formValue.action.actionKind,
        };

        // Add specific fields based on action kind
        if (formValue.action.actionKind === ActionKind.SendMessage) {
          action.templateName = formValue.action.templateName;
        }

        // Set the action on the trigger for all trigger kinds except ConversationDataChanged
        trigger.action = action;
      }

      // Build final request object
      const result = {
        conversationTypeId: formValue.conversationTypeId || null,
        trigger: trigger,
      };

      this.#dialogRef.close(result);
    }
  }

  private setupJsonPathPrefixHandler(): void {
    if (!this.jsonPathInput) return;

    const jsonPathControl = this.workflowForm.get('trigger.jsonPath');
    if (!jsonPathControl) return;

    let currentValue = jsonPathControl.value || '';
    if (currentValue.startsWith(this.jsonPathPrefix)) {
      currentValue = currentValue.substring(this.jsonPathPrefix.length);
      jsonPathControl.setValue(currentValue);
    }
  }

  getFullJsonPath(): string {
    const jsonPathControl = this.workflowForm.get('trigger.jsonPath');
    if (!jsonPathControl) return '';

    const userInput = jsonPathControl.value || '';
    if (userInput.startsWith(this.jsonPathPrefix)) {
      return userInput;
    }
    return this.jsonPathPrefix + userInput;
  }

  updateRuleErrors(errors: string[]): void {
    this.ruleEditorErrors.set(errors);
  }

  canSave(): boolean {
    if (!this.workflowForm.valid) {
      return false;
    }

    const triggerKind = this.workflowForm.get('trigger.triggerKind')?.value;

    // For conversation data changed triggers, we need valid rule expression
    if (triggerKind === TriggerKind.ConversationDataChanged) {
      // Ensure both the form is valid AND the rule is valid
      return this.ruleValid() && this.ruleExpression()?.trim().length > 0;
    }

    // For other trigger types, check that action section is valid
    const actionKind = this.workflowForm.get('action.actionKind')?.value;
    if (actionKind === ActionKind.SendMessage) {
      return !!this.workflowForm.get('action.templateName')?.valid;
    }

    return true;
  }
}
